import { useRef, useState, useCallback } from "react";

interface GeoJsonDropzoneProps {
  onFileLoad: (geojson: any) => void;
  t: (key: string) => string;
}

export function GeoJsonDropzone({ onFileLoad, t }: GeoJsonDropzoneProps) {
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUserInteracting, setIsUserInteracting] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer?.files?.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFile(e.target.files[0]);
      }
      e.target.value = "";
    },
    []
  );

  const processFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const geojson = JSON.parse(event.target?.result as string);
          onFileLoad(geojson);
        } catch (err) {
          console.error("Invalid GeoJSON file", err);
        }
      };
      reader.readAsText(file);
    },
    [onFileLoad]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isUserInteracting) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      fileInputRef.current?.click();
    },
    [isUserInteracting]
  );

  const handleMouseDown = useCallback(() => {
    setIsUserInteracting(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setTimeout(() => setIsUserInteracting(false), 100);
  }, []);

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsUserInteracting(true);
            fileInputRef.current?.click();
            setTimeout(() => setIsUserInteracting(false), 100);
          }
        }}
        style={{
          marginTop: "20px",
          padding: "40px",
          border: `2px dashed ${dragActive ? "#086AA9" : "#999"}`,
          borderRadius: "8px",
          backgroundColor: dragActive ? "rgba(8, 106, 169, 0.1)" : "#f9f9f9",
          textAlign: "center",
          color: "#333",
          cursor: "pointer",
          outline: "none",
        }}
      >
        {dragActive ? t("dropTheFile") : t("dragAndDrop")}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".geojson,application/geo+json,application/json"
        style={{
          display: "none",
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          opacity: 0,
          pointerEvents: "none",
        }}
        onChange={handleFileChange}
        tabIndex={-1}
      />
    </div>
  );
}
